import { lonLatToMercator, eventBurnedBBox, quantizeLonLatBBox } from "./geo";
import type { BBox } from "./types";

/** Public FIRMS regional CSVs — no MAP_KEY required. Europe files, continuously updated. */
export const FIRMS_FEEDS = [
  {
    source: "VIIRS_SNPP",
    url: "https://firms.modaps.eosdis.nasa.gov/data/active_fire/suomi-npp-viirs-c2/csv/SUOMI_VIIRS_C2_Europe_7d.csv",
  },
  {
    source: "VIIRS_NOAA20",
    url: "https://firms.modaps.eosdis.nasa.gov/data/active_fire/noaa-20-viirs-c2/csv/J1_VIIRS_C2_Europe_7d.csv",
  },
  {
    source: "VIIRS_NOAA21",
    url: "https://firms.modaps.eosdis.nasa.gov/data/active_fire/noaa-21-viirs-c2/csv/J2_VIIRS_C2_Europe_7d.csv",
  },
  {
    source: "MODIS",
    url: "https://firms.modaps.eosdis.nasa.gov/data/active_fire/modis-c6.1/csv/MODIS_C6_1_Europe_7d.csv",
  },
  // Sentinel-3 is not a FIRMS public CSV. Point ingest: EFFIS WFS `s3.hs` in shared/s3-list.ts.
] as const;

/** Hourly ingest uses the 24h file once that sensor already has rows in D1. */
export function firmsFeedUrl(url: string, span: "7d" | "24h"): string {
  return span === "24h" ? url.replace(/_7d\.csv$/i, "_24h.csv") : url;
}

export const CLAIM =
  "Near-real-time satellite-observed fire activity across Europe. Not every wildfire, and not within 10 minutes.";

export const RETENTION_DAYS = 8;

export const ADAGUC_WMS = "https://adaguc.lsasvcs.ipma.pt/adaguc-server";
/** LSA SAF documents this GeoServer for MTG GeoColour next to their FRP WMS. */
export const EUMETVIEW_WMS = "https://view.eumetsat.int/geoserver/wms";

/** One WMS layer the Worker can proxy as XYZ tiles. */
export type WmsLayerSpec = {
  layer: string;
  style: string;
  demo: boolean;
  /** ADAGUC dataset. Omit on EUMETView. */
  dataset?: string;
  /** Defaults to ADAGUC_WMS. */
  wms?: string;
  format?: string;
  /** When set, the proxy stamps TIME to the latest slot minus lag. */
  timeStepMin?: number;
  timeLagMin?: number;
};

/** LSA SAF ADAGUC WMS (CC BY 4.0). Proxied by the Worker to avoid browser CORS. */
export const WMS_LAYERS: Record<string, WmsLayerSpec> = {
  "mtg-frp": {
    dataset: "MTG-FRP",
    layer: "FRP",
    style: "pointdata/point",
    // Demonstration product — UI must say so. Map toggle id is `mtg`.
    // Worker keeps only the red palette (~≥50 MW); blue ag-burn dots are dropped.
    demo: true,
    timeStepMin: 10,
    timeLagMin: 20,
  },
  "msg-frp": {
    dataset: "MSG-FRP",
    layer: "FRP",
    style: "pointdata/point",
    demo: false,
  },
  risk: {
    dataset: "MSG-FRMv3",
    layer: "Risk",
    style: "risk_map_style/nearest",
    demo: true,
  },
  // Land-surface temperature — never mixed into the fire heatmap.
  lst: {
    dataset: "MTG-LST",
    layer: "LST",
    style: "rainbow/nearest",
    demo: false,
    timeStepMin: 60,
    timeLagMin: 30,
  },
  // 10-minute FCI photograph. LSA SAF tells you to overlay this on FRP.
  "mtg-geo": {
    wms: EUMETVIEW_WMS,
    layer: "mtg_fd:rgb_geocolour",
    style: "raster",
    format: "image/jpeg",
    demo: false,
    timeStepMin: 10,
    timeLagMin: 20,
  },
};

/**
 * Latest product slot the upstream is likely to have.
 * MTG FRP / GeoColour publish every 10 min and run ~20 min late.
 */
export function latestProductTimeIso(stepMin: number, lagMin: number, nowMs = Date.now()): string {
  const step = stepMin * 60 * 1000;
  const t = Math.floor((nowMs - lagMin * 60 * 1000) / step) * step;
  return new Date(t).toISOString().replace(".000Z", "Z");
}

/** Aim for this many GeoColour photos in a ~20 s Play — not every 10-minute slot. */
export const REPLAY_SAT_TARGET_FRAMES = 24;

/**
 * Snap the playhead to a GeoColour slot, skipping 10-min frames on long windows.
 * A 24 h play uses hourly photos; 1 h still uses the native 10 min product.
 */
export function replayProductSlotIso(
  playheadMs: number,
  windowMs: number,
  stepMin: number,
  lagMin: number,
  nowMs = Date.now(),
): string {
  const latestMs = Date.parse(latestProductTimeIso(stepMin, lagMin, nowMs));
  const t = Math.min(playheadMs, latestMs);
  const native = stepMin * 60 * 1000;
  const needed = windowMs / REPLAY_SAT_TARGET_FRAMES;
  // 10 / 20 / 30 min, then 1 / 2 / 3 / 6 / 12 h — stays on the product clock.
  const skipLadder = [1, 2, 3, 6, 12, 18, 36, 72];
  let skip = skipLadder[skipLadder.length - 1]!;
  for (const m of skipLadder) {
    if (m * native >= needed) {
      skip = m;
      break;
    }
  }
  const step = skip * native;
  const snapped = Math.floor(t / step) * step;
  return new Date(snapped).toISOString().replace(".000Z", "Z");
}

/** NASA GIBS daily composites. CORS is open; we still proxy so empty frames stay hidden. */
export const WORLDVIEW_SNAPSHOT = "https://wvs.earthdata.nasa.gov/api/v1/snapshot";

/** Open-Meteo forecast — no API key. Point wind for the panel; multi-point grid for the map layer. */
export const OPEN_METEO_FORECAST = "https://api.open-meteo.com/v1/forecast";

export const FIRMS_HOME = "https://firms.modaps.eosdis.nasa.gov/";
export const WORLDVIEW_APP = "https://worldview.earthdata.nasa.gov/";
export const NOMINATIM_REVERSE = "https://nominatim.openstreetmap.org/reverse";
export const RESEND_EMAILS = "https://api.resend.com/emails";

/**
 * Public site — sitemap, canonical, og:url, share cards, and alert links.
 * Never put the workers.dev hostname here. That is IGNIS_WORKER_ORIGIN.
 */
export const IGNIS_ORIGIN = "https://ignis-eu.com";
/** Cloudflare Worker hostname. Fine for wrangler / deploy; not the public URL. */
export const IGNIS_WORKER_ORIGIN = "https://ignis.jonathanbertholet.workers.dev";
export const IGNIS_STAGING_ORIGIN = "https://ignis-staging.jonathanbertholet.workers.dev";

/** Same job as the :08 cron. Key goes in `x-ingest-key`, not the URL. */
export function ingestApiUrl(
  origin: string,
  opts: { mtg?: boolean; force?: boolean } = {},
): string {
  const u = new URL("/api/ingest", origin);
  if (opts.mtg) u.searchParams.set("source", "mtg");
  // Re-download FIRMS even when the ETag has not changed (parser / filter deploys).
  if (opts.force) u.searchParams.set("force", "1");
  return u.toString();
}

/** Staging Worker sets ENVIRONMENT=staging; production leaves it unset. */
export function ignisOrigin(env?: { ENVIRONMENT?: string }): string {
  return env?.ENVIRONMENT === "staging" ? IGNIS_STAGING_ORIGIN : IGNIS_ORIGIN;
}

/** Browser host must not win — visiting workers.dev still canonicalizes to ignis-eu.com. */
export function publicOriginFromHost(hostname: string): string {
  return hostname.includes("ignis-staging") ? IGNIS_STAGING_ORIGIN : IGNIS_ORIGIN;
}

/** Staging must not be indexed — robots.txt and HTML meta both key off this. */
export function isStagingEnv(env?: { ENVIRONMENT?: string }): boolean {
  return env?.ENVIRONMENT === "staging";
}

/** OSM Overpass — public webcam URLs near a selected fire. No API key. */
export const OVERPASS_INTERPRETER = "https://overpass-api.de/api/interpreter";

/**
 * Copernicus EFFIS/GWIS WMS (ECMWF FWI + Sentinel-3 hotspots).
 * CORS is open — the map talks to this host directly so tiles do not count as Worker requests.
 */
export const EFFIS_GWIS_WMS = "https://maps.effis.emergency.copernicus.eu/gwis";

/** UTC calendar date, optionally shifted. EFFIS FWI uses this as WMS TIME (today + forecast). */
export function utcIsoDate(plusDays = 0, now = new Date()): string {
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + plusDays));
  return d.toISOString().slice(0, 10);
}

/** MapLibre raster template. `{bbox-epsg-3857}` must stay unencoded. TIME is optional (burned-area layers ignore it). */
export function effisGwisTileUrl(layer: string, timeIsoDate?: string): string {
  const q = new URLSearchParams({
    SERVICE: "WMS",
    VERSION: "1.1.1",
    REQUEST: "GetMap",
    LAYERS: layer,
    STYLES: "default",
    FORMAT: "image/png",
    TRANSPARENT: "TRUE",
    SRS: "EPSG:3857",
    WIDTH: "256",
    HEIGHT: "256",
  });
  if (timeIsoDate) q.set("TIME", timeIsoDate);
  return `${EFFIS_GWIS_WMS}?${q.toString()}&BBOX={bbox-epsg-3857}`;
}

/** NW, NE, SE, SW — MapLibre image-source order, same as the GeoColour frame. */
export function eventBurnedCoordinates(
  box: BBox,
): [[number, number], [number, number], [number, number], [number, number]] {
  return [
    [box.west, box.north],
    [box.east, box.north],
    [box.east, box.south],
    [box.west, box.south],
  ];
}

/**
 * One EFFIS RDA GetMap for a fire's neighbourhood — not the Europe XYZ tiles
 * the Burned button uses. Transparent PNG; empty if nothing is mapped yet.
 */
export function effisBurnedMapUrl(box: BBox, maxPx = 768): string {
  const [x0, y0] = lonLatToMercator(box.west, box.south);
  const [x1, y1] = lonLatToMercator(box.east, box.north);
  const wM = Math.abs(x1 - x0);
  const hM = Math.abs(y1 - y0);
  const wide = wM >= hM;
  const width = wide ? maxPx : Math.max(64, Math.round(maxPx * (wM / Math.max(hM, 1))));
  const height = wide ? Math.max(64, Math.round(maxPx * (hM / Math.max(wM, 1)))) : maxPx;
  const q = new URLSearchParams({
    SERVICE: "WMS",
    VERSION: "1.1.1",
    REQUEST: "GetMap",
    LAYERS: "nrt.ba.week",
    STYLES: "default",
    FORMAT: "image/png",
    TRANSPARENT: "TRUE",
    SRS: "EPSG:3857",
    WIDTH: String(width),
    HEIGHT: String(height),
    BBOX: `${Math.min(x0, x1)},${Math.min(y0, y1)},${Math.max(x0, x1)},${Math.max(y0, y1)}`,
  });
  return `${EFFIS_GWIS_WMS}?${q.toString()}`;
}

/** Quantized neighbourhood frame. Null only if we have no pin to hang the box on. */
export function eventBurnedFrame(
  points: Array<[number, number]>,
  fallback: [number, number],
): {
  url: string;
  coordinates: [[number, number], [number, number], [number, number], [number, number]];
  key: string;
} | null {
  const raw = eventBurnedBBox(points, fallback);
  if (!raw) return null;
  const box = quantizeLonLatBBox(raw);
  return {
    url: effisBurnedMapUrl(box),
    coordinates: eventBurnedCoordinates(box),
    key: `${box.west.toFixed(3)},${box.south.toFixed(3)},${box.east.toFixed(3)},${box.north.toFixed(3)}`,
  };
}
